// Goals Service - Firestore Integration
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  onSnapshot,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { db } from "@/lib/firebase";

const COLLECTION_NAME = "goals";

// Get all goals for a user (including shared goals)
export const getGoals = async (userId) => {
  try {
    // Get goals where user is owner
    const ownerQuery = query(
      collection(db, COLLECTION_NAME),
      where("userId", "==", userId)
    );
    
    // Get goals where user is participant
    const participantQuery = query(
      collection(db, COLLECTION_NAME),
      where("participants", "array-contains", userId)
    );
    
    const [ownerSnapshot, participantSnapshot] = await Promise.all([
      getDocs(ownerQuery),
      getDocs(participantQuery)
    ]);
    
    const goalsMap = new Map();
    
    ownerSnapshot.docs.forEach(doc => {
      goalsMap.set(doc.id, { id: doc.id, ...doc.data() });
    });
    
    participantSnapshot.docs.forEach(doc => {
      if (!goalsMap.has(doc.id)) {
        goalsMap.set(doc.id, { id: doc.id, ...doc.data() });
      }
    });
    
    return Array.from(goalsMap.values());
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Subscribe to goals (real-time updates)
export const subscribeToGoals = (userId, callback) => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where("userId", "==", userId)
  );
  
  return onSnapshot(q, (snapshot) => {
    const goals = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(goals);
  }, (error) => {
    // Error silenced for production
  });
};

// Add a new goal
export const addGoal = async (userId, goalData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...goalData,
      userId,
      currentAmount: 0,
      status: "in_progress",
      movements: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: docRef.id, ...goalData, currentAmount: 0, status: "in_progress", movements: [] };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update a goal
export const updateGoal = async (goalId, goalData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, goalId);
    await updateDoc(docRef, {
      ...goalData,
      updatedAt: serverTimestamp()
    });
    return { id: goalId, ...goalData };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete a goal
export const deleteGoal = async (goalId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, goalId));
    return goalId;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Get goal by ID
export const getGoalById = async (goalId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, goalId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Add movement to a goal (deposit or withdrawal)
export const addMovement = async (goalId, movement) => {
  try {
    const goalRef = doc(db, COLLECTION_NAME, goalId);
    const goalSnap = await getDoc(goalRef);
    
    if (!goalSnap.exists()) {
      throw new Error("Goal not found");
    }
    
    const goalData = goalSnap.data();
    const movementWithId = {
      ...movement,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const newCurrentAmount = movement.type === "deposit"
      ? goalData.currentAmount + movement.amount
      : goalData.currentAmount - movement.amount;
    
    const newStatus = newCurrentAmount >= goalData.targetAmount ? "completed" : "in_progress";
    
    await updateDoc(goalRef, {
      movements: arrayUnion(movementWithId),
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    return {
      ...goalData,
      id: goalId,
      movements: [...(goalData.movements || []), movementWithId],
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus
    };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Update movement in a goal
export const updateMovement = async (goalId, movementId, movementData) => {
  try {
    const goalRef = doc(db, COLLECTION_NAME, goalId);
    const goalSnap = await getDoc(goalRef);
    
    if (!goalSnap.exists()) {
      throw new Error("Goal not found");
    }
    
    const goalData = goalSnap.data();
    const updatedMovements = goalData.movements.map(m => 
      m.id === movementId ? { ...m, ...movementData } : m
    );
    
    // Recalculate current amount
    const newCurrentAmount = updatedMovements.reduce((sum, m) => 
      m.type === "deposit" ? sum + m.amount : sum - m.amount, 0
    );
    
    const newStatus = newCurrentAmount >= goalData.targetAmount ? "completed" : "in_progress";
    
    await updateDoc(goalRef, {
      movements: updatedMovements,
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    return {
      ...goalData,
      id: goalId,
      movements: updatedMovements,
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus
    };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

// Delete movement from a goal
export const deleteMovement = async (goalId, movementId) => {
  try {
    const goalRef = doc(db, COLLECTION_NAME, goalId);
    const goalSnap = await getDoc(goalRef);
    
    if (!goalSnap.exists()) {
      throw new Error("Goal not found");
    }
    
    const goalData = goalSnap.data();
    const updatedMovements = goalData.movements.filter(m => m.id !== movementId);
    
    // Recalculate current amount
    const newCurrentAmount = updatedMovements.reduce((sum, m) => 
      m.type === "deposit" ? sum + m.amount : sum - m.amount, 0
    );
    
    const newStatus = newCurrentAmount >= goalData.targetAmount ? "completed" : "in_progress";
    
    await updateDoc(goalRef, {
      movements: updatedMovements,
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    return {
      ...goalData,
      id: goalId,
      movements: updatedMovements,
      currentAmount: Math.max(0, newCurrentAmount),
      status: newStatus
    };
  } catch (error) {
    // Error silenced for production
    throw error;
  }
};

export default {
  getGoals,
  subscribeToGoals,
  addGoal,
  updateGoal,
  deleteGoal,
  getGoalById,
  addMovement,
  updateMovement,
  deleteMovement
};
